import { type ExperienceSocialConnector } from '@logto/schemas';
import { Button } from '@unlimitai/ui';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import SocialLinkButton from '@/components/Button/SocialLinkButton';
import useSocial from '@/containers/SocialSignInList/use-social';
import TermsAndPrivacyLinks from '@/containers/TermsAndPrivacyLinks';
import { LoadingIcon } from '@/shared/components/LoadingLayer';
import { getWeChatResumeUrl } from '@/utils/embedded-wechat';

import styles from './index.module.scss';
import { buildWeChatFrameUrl } from './utils';

type Props = {
  readonly connector: ExperienceSocialConnector;
  readonly onOtherMethods: () => void;
  readonly isPreview?: boolean;
};

const WeChatSignIn = ({ connector, onOtherMethods, isPreview = false }: Props) => {
  const { t } = useTranslation();
  const { theme } = useContext(PageContext);
  const frameRef = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    const resume = (event: MessageEvent) => {
      const url = getWeChatResumeUrl(event, frameRef.current ?? undefined, window.location.origin);
      if (url) {
        window.location.replace(url);
      }
    };
    window.addEventListener('message', resume);
    return () => {
      window.removeEventListener('message', resume);
    };
  }, []);
  const { prepareSocialSignIn, invokeSocialSignIn } = useSocial();
  const [frameUrl, setFrameUrl] = useState<string>();
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const requestRef = useRef<{ attempt: number; promise: Promise<string | undefined> }>();

  useEffect(() => {
    if (isPreview) {
      return;
    }
    const lifecycle = new AbortController();
    // StrictMode may replay this effect; reuse its in-flight request instead of invalidating OAuth state.
    if (requestRef.current?.attempt !== attempt) {
      // eslint-disable-next-line @silverhand/fp/no-mutation -- Retain the one authorization request for this QR generation.
      requestRef.current = { attempt, promise: prepareSocialSignIn(connector) };
    }
    const request = requestRef.current.promise;
    const load = async () => {
      try {
        const uri = await request;
        if (lifecycle.signal.aborted) {
          return;
        }
        if (!uri) {
          setFailed(true);
          return;
        }
        setFrameUrl(buildWeChatFrameUrl(uri, theme, true));
      } catch {
        if (!lifecycle.signal.aborted) {
          setFailed(true);
        }
      }
    };
    void load();
    return () => {
      lifecycle.abort();
    };
  }, [attempt, connector, isPreview, prepareSocialSignIn, theme]);

  return (
    <section className={styles.section} aria-label={t('action.wechat_scan')}>
      <div className={styles.frame}>
        {frameUrl ? (
          // WeChat needs scripts and cookies; embedded login deliberately excludes top navigation.
          // eslint-disable-next-line react/iframe-missing-sandbox -- WeChat OAuth requires scripts and its origin; ancestor navigation remains blocked and callbacks are source-checked.
          <iframe
            ref={frameRef}
            data-unlimitai-wechat-embedded="true"
            sandbox="allow-scripts allow-same-origin allow-forms"
            title={t('action.wechat_scan')}
            src={frameUrl}
            className={styles.qr}
            onError={() => {
              setFailed(true);
            }}
          />
        ) : (
          <div className={styles.placeholder} role="status">
            {!failed && !isPreview && <LoadingIcon />}
            <span>
              {t(
                isPreview
                  ? 'action.wechat_scan'
                  : failed
                    ? 'action.wechat_retry'
                    : 'action.wechat_loading'
              )}
            </span>
          </div>
        )}
      </div>
      <div className={styles.actions}>
        <Button
          variant="ghost"
          type="button"
          disabled={isPreview || (!frameUrl && !failed)}
          onClick={() => {
            setFrameUrl(undefined);
            setFailed(false);
            setAttempt((value) => value + 1);
          }}
        >
          {t('action.wechat_retry')}
        </Button>
        <Button variant="ghost" type="button" onClick={onOtherMethods}>
          {t('action.other_sign_in_methods')}
        </Button>
      </div>
      {failed && (
        <SocialLinkButton
          {...connector}
          onClick={() => {
            void invokeSocialSignIn(connector);
          }}
        />
      )}
      <TermsAndPrivacyLinks className={styles.terms} />
    </section>
  );
};

export default WeChatSignIn;
